import { apiFetch } from '../../../shared/api/apiFetch'

export interface HuntingGroundAreaDto {
  id: string
  name: string | null
  areaType: 'hunt' | 'sub' | 'forbidden'
  /**
   * The first entry of `rings`, repeated for clients built before the ring list
   * existed. Optional here rather than required, because the release after the one
   * that introduced `rings` stops sending it.
   */
  coordinates?: { latitude: number; longitude: number }[]
  /**
   * Every ring of the area: outer rings first, then interior rings. Optional only
   * because an API deployed from an older tag does not send it — the two are on
   * independently tagged trains with no ordering between them, so both shapes have
   * to be readable for the length of the window between deploys.
   */
  rings?: { latitude: number; longitude: number }[][]
  /** How many leading entries of `rings` are outers. */
  outerRingCount?: number
  /**
   * `"storage"` when the real outline is an imported or complex property-parcel blob rather than the inline
   * coordinate list. The editor uses it to decide whether reshaping is on offer: the
   * inline list holds one ring, so an outline with several parts or an excluded lake
   * cannot be written back and the API refuses such a write.
   */
  coordinateSource?: string | null
  /**
   * The stored `[SW, NE]` bounding box. For a `coordinateSource: "storage"` area the
   * polygon is offloaded, so this is the only geometry the API can send — and the
   * only thing the map can be framed against.
   */
  bounds?: { latitude: number; longitude: number }[]
  color?: string | null
  // Provenance of an area sourced from cadastral property parcels: each picked
  // parcel is its own area, all sharing one `areaGroup`, marked
  // `areaSource: "property"` — matching how mobile stores property parcels.
  areaSource?: string | null
  areaGroup?: string | null
}

interface HuntingGroundsResponse {
  areas: HuntingGroundAreaDto[]
}

interface HuntingGroundBody {
  name: string | null
  areaType: string
  /**
   * Sent alongside `rings` for as long as an API deployed from an older tag might
   * receive this body: that build reads only this field, and a body without it would
   * be rejected for having no outline at all.
   */
  coordinates: { latitude: number; longitude: number }[]
  /**
   * The whole ring list. For an outline the API stores as a blob this is the echo
   * that lets it preserve the geometry through a rename or recolour; truncating it
   * to the first ring is the write that used to destroy every other part.
   */
  rings?: { latitude: number; longitude: number }[][]
  /**
   * How many leading entries of `rings` are outers; the rest are holes. The API needs it
   * to store several rings as one polygon with holes rather than several parts, and to
   * refuse a body whose count does not fit its rings.
   *
   * This type is shared by `createHuntingGround` and `updateHuntingGround`, but the
   * update endpoint's request type has no such member — System.Text.Json's default
   * behaviour is to ignore an unmapped property rather than reject it, so sending this
   * field on a PUT is silently dropped rather than erroring. That is fine: an update
   * writes and compares only the first ring (see `rings` on `HuntingGroundAreaDto` and
   * `EffectiveRing` server-side), so it has nothing to interpret an outer/hole split
   * against.
   */
  outerRingCount: number
  color?: string | null
  areaSource?: string | null
  areaGroup?: string | null
}

const base = (groupId: string) =>
  `/clubhouse/groups/${encodeURIComponent(groupId)}/hunting-grounds`

export async function listGroupHuntingGrounds(
  groupId: string,
  token: string,
): Promise<HuntingGroundsResponse> {
  return apiFetch<HuntingGroundsResponse>(`${base(groupId)}`, { token })
}

export async function createHuntingGround(
  groupId: string,
  body: HuntingGroundBody,
  token: string,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`${base(groupId)}`, {
    method: 'POST',
    body: JSON.stringify(body),
    token,
  })
}

export async function updateHuntingGround(
  groupId: string,
  id: string,
  body: HuntingGroundBody,
  token: string,
): Promise<void> {
  await apiFetch<undefined>(`${base(groupId)}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
    token,
  })
}

export async function deleteHuntingGround(
  groupId: string,
  id: string,
  token: string,
): Promise<void> {
  await apiFetch<undefined>(`${base(groupId)}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    token,
  })
}
